import { isEmpty } from "underscore";
import { onSnapshot, applySnapshot } from "mobx-state-tree";

import Backbone from "pages/common/lib/backbone-helpers";
import Utils from "pages/common/lib/grabyo-util";

import FileCollection from "../state/collection/files";
import { UPLOAD_ID_LENGTH } from "../state/store/uploader/uploader";

export const BackwardsCompatibleFilesCollection = FileCollection.extend({
    model: Backbone.Model.extend({})
});

/**
 * @function
 * @summary Converts an UploadStore instance to a legacy Backbone File model object
 *
 * @param {UploadStore} upload Upload store
 *
 * @returns {Object} File model object
 */
export const parseUploadToFileModel = (upload) => {
    const {
        id,
        status,
        progress,
        errorMessages,
        fileName,
        storedURL,
        url,
        mimetype,
        fileSize
    } = upload.toJSON();
    return {
        id,
        status,
        progress,
        errorMessage: !errorMessages ? `` : errorMessages.join(`\n`),
        filename: fileName,
        srcUrl: storedURL || url,
        dataUrl: storedURL || url,
        url: storedURL || url,
        mimetype,
        size: fileSize,
        // Non model properties
        s3Filename: upload.s3Filename
    };
};

/**
 * @function
 * @summary Converts an File model object to an UploadStore model object
 *
 * @param {Object} file File model object
 *
 * @returns {Object} UploadStore model object
 */
export const parseFileModelToUpload = (file) => {
    const {
        id,
        status,
        progress,
        errorMessage,
        filename,
        srcUrl,
        url,
        dataUrl,
        mimetype,
        size
    } = file;
    return {
        id,
        status,
        progress: progress / 100,
        // Make sure error message doesn't get set to empty string
        errorMessages: !isEmpty(errorMessage) ? [errorMessage] : undefined,
        fileName: filename,
        fileSize: size,
        url: srcUrl,
        storedURL: url || dataUrl,
        mimetype
    };
};

/**
 * @function
 * @summary - Syncs the uploader store with the legacy Backbone Files collection, keep changes to in one side of the state matching the other
 *
 * @param {UploaderStore} store Uploader store
 * @param {Object} collection Files collection
 *
 * @returns {Function} Listener cleanup fn
 */
export const syncStoreAndCollection = (store, collection) => {
    let syncing;

    // Reflect updates to the store into Backbone collection
    const disposer = onSnapshot(store.uploads, () => {
        if (syncing) return;
        syncing = true;

        collection.set(store.uploadsArray.map(parseUploadToFileModel));

        syncing = false;
    });

    // Reflect updates to the Backbone collection into the store
    const onCollectionChange = () => {
        if (syncing) return;
        syncing = true;

        const uploads = {};
        collection.toJSON().forEach((model) => {
            if (!model.id) {
                model.id = Utils.generateGrabyoId(UPLOAD_ID_LENGTH);
            }
            uploads[model.id] = parseFileModelToUpload(model);
        });
        applySnapshot(store.uploads, uploads);

        syncing = false;
    };

    collection.on(`add remove change reset`, onCollectionChange);

    return () => {
        disposer();
        collection.off(null, onCollectionChange);
    };
};
